import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { AlertTriangle, Upload, X } from "lucide-react"
import { FormModal } from "@/components/ui/form-modal"
import { useToast } from "@/hooks/use-toast"
import { useAuth } from "@/hooks/useAuth"
import { useCreateIncident } from "@/hooks/useIncidents"
import { useUnitsForIncidents } from "@/hooks/useUnitsForIncidents"
import { validateField, sanitizeInput, ValidationRules } from "@/utils/inputValidation"

interface NovaOcorrenciaModalProps {
  children?: React.ReactNode
  triggerButton?: React.ReactNode
}

interface ValidationErrors {
  title?: string
  description?: string
  category?: string
  residentName?: string
}

const NovaOcorrenciaModal = ({ children, triggerButton }: NovaOcorrenciaModalProps) => {
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [priority, setPriority] = useState("medium")
  const [category, setCategory] = useState("")
  const [unitId, setUnitId] = useState("")
  const [residentName, setResidentName] = useState("")
  const [photoFile, setPhotoFile] = useState<File | null>(null)
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})
  
  const { toast } = useToast()
  const { profile } = useAuth()
  const createIncident = useCreateIncident()
  const { data: units = [] } = useUnitsForIncidents()

  const canSpecifyResident = profile?.role !== 'morador'

  const validateForm = (): boolean => {
    const errors: ValidationErrors = {}
    
    const titleValidation = validateField(title, 'title')
    if (!titleValidation.isValid) {
      errors.title = titleValidation.error
    }
    
    const descriptionValidation = validateField(description, 'description')
    if (!descriptionValidation.isValid) {
      errors.description = descriptionValidation.error
    }
    
    if (category && category.trim()) {
      const categoryValidation = validateField(category, 'name')
      if (!categoryValidation.isValid) {
        errors.category = categoryValidation.error
      }
    }

    if (canSpecifyResident && residentName && residentName.trim()) {
      const residentValidation = validateField(residentName, 'name')
      if (!residentValidation.isValid) {
        errors.residentName = residentValidation.error
      }
    }
    
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!profile) {
      toast({
        title: "Erro",
        description: "Usuário não autenticado.",
        variant: "destructive",
      })
      return
    }
    
    if (!validateForm()) {
      toast({
        title: "Erro de Validação",
        description: "Por favor, corrija os erros no formulário.",
        variant: "destructive",
      })
      return
    }

    try {
      // Prepare the correct data structure for the createIncident mutation
      let finalDescription = sanitizeInput(description)
      
      // Se for porteiro ou síndico, adicionar nome do morador na descrição
      if (canSpecifyResident && residentName.trim()) {
        finalDescription = `Morador: ${sanitizeInput(residentName)}\n\n${finalDescription}`
      }

      const incidentData = {
        title: sanitizeInput(title),
        description: finalDescription,
        priority,
        category: category ? sanitizeInput(category) : null,
        unit_id: unitId || null,
        photoFile: photoFile || undefined
      }

      console.log("Creating incident with data:", incidentData)

      await createIncident.mutateAsync(incidentData)

      // Reset form
      setTitle("")
      setDescription("")
      setPriority("medium")
      setCategory("")
      setUnitId("")
      setResidentName("")
      setPhotoFile(null)
      setValidationErrors({})
      setOpen(false)
    } catch (error: any) {
      console.error("Error creating incident:", error)
      toast({
        title: "Erro",
        description: error.message || "Erro ao registrar ocorrência.",
        variant: "destructive",
      })
    }
  }

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Verificar tipo de arquivo
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Erro",
          description: "Por favor, selecione apenas arquivos de imagem.",
          variant: "destructive",
        })
        return
      }
      
      // Verificar tamanho (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "Erro",
          description: "A imagem deve ter no máximo 5MB.",
          variant: "destructive",
        })
        return
      }
      
      setPhotoFile(file)
    }
  }

  const handleRemovePhoto = () => {
    setPhotoFile(null)
  }

  const clearValidationError = (field: keyof ValidationErrors) => {
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const formContent = (
    <>
      <div className="grid gap-2">
        <Label htmlFor="title">Título *</Label>
        <Input
          id="title"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value)
            clearValidationError('title')
          }}
          maxLength={ValidationRules.title.maxLength}
          required
          className={validationErrors.title ? "border-red-500" : ""}
        />
        {validationErrors.title && (
          <p className="text-sm text-red-500">{validationErrors.title}</p>
        )}
        <p className="text-xs text-gray-500">
          {title.length}/{ValidationRules.title.maxLength} caracteres
        </p>
      </div>

      {canSpecifyResident && (
        <>
          <div className="grid gap-2">
            <Label htmlFor="unit">Unidade/Apartamento</Label>
            <Select value={unitId} onValueChange={setUnitId}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione a unidade (opcional)" />
              </SelectTrigger>
              <SelectContent>
                {units.map((unit) => (
                  <SelectItem key={unit.id} value={unit.id}>
                    {unit.building ? `${unit.building}-` : ""}{unit.number}
                    {unit.unit_details?.[0]?.owner_name && ` - ${unit.unit_details[0].owner_name}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="residentName">Nome do Morador</Label>
            <Input
              id="residentName"
              value={residentName}
              onChange={(e) => {
                setResidentName(e.target.value)
                clearValidationError('residentName')
              }}
              placeholder="Nome do morador envolvido (opcional)"
              maxLength={ValidationRules.name.maxLength}
              className={validationErrors.residentName ? "border-red-500" : ""}
            />
            {validationErrors.residentName && (
              <p className="text-sm text-red-500">{validationErrors.residentName}</p>
            )}
          </div>
        </>
      )}

      <div className="grid gap-2">
        <Label htmlFor="category">Categoria</Label>
        <Input
          id="category"
          value={category}
          onChange={(e) => {
            setCategory(e.target.value)
            clearValidationError('category')
          }}
          placeholder="Ex: Manutenção, Segurança, Limpeza"
          maxLength={ValidationRules.name.maxLength}
          className={validationErrors.category ? "border-red-500" : ""}
        />
        {validationErrors.category && (
          <p className="text-sm text-red-500">{validationErrors.category}</p>
        )}
      </div>

      <div className="grid gap-2">
        <Label htmlFor="priority">Prioridade *</Label>
        <Select value={priority} onValueChange={setPriority} required>
          <SelectTrigger>
            <SelectValue placeholder="Selecione a prioridade" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="low">Baixa</SelectItem>
            <SelectItem value="medium">Média</SelectItem>
            <SelectItem value="high">Alta</SelectItem>
            <SelectItem value="urgent">Urgente</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="description">Descrição *</Label>
        <Textarea
          id="description"
          value={description}
          onChange={(e) => {
            setDescription(e.target.value)
            clearValidationError('description')
          }}
          placeholder="Descreva detalhadamente a ocorrência"
          rows={4}
          maxLength={ValidationRules.description.maxLength}
          required
          className={validationErrors.description ? "border-red-500" : ""}
        />
        {validationErrors.description && (
          <p className="text-sm text-red-500">{validationErrors.description}</p>
        )}
        <p className="text-xs text-gray-500">
          {description.length}/{ValidationRules.description.maxLength} caracteres
        </p>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="photo">Foto (opcional)</Label>
        {!photoFile ? (
          <div className="flex items-center gap-2">
            <Input
              id="photo"
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => document.getElementById('photo')?.click()}
              className="w-full"
            >
              <Upload className="h-4 w-4 mr-2" />
              Selecionar Foto
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2 p-2 border rounded-md">
            <span className="flex-1 text-sm">{photoFile.name}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleRemovePhoto}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
        <p className="text-xs text-gray-500">
          Máximo 5MB. Formatos aceitos: JPG, PNG, GIF
        </p>
      </div>
    </>
  )

  return (
    <FormModal
      open={open}
      onOpenChange={setOpen}
      title="Registrar Nova Ocorrência"
      description="Registre uma nova ocorrência no condomínio."
      onSubmit={handleSubmit}
      submitButtonText="Registrar Ocorrência"
      loading={createIncident.isPending}
      formContent={formContent}
      triggerButton={triggerButton || (
        <Button>
          <AlertTriangle className="h-4 w-4 mr-2" />
          Nova Ocorrência
        </Button>
      )}
    >
      {children}
    </FormModal>
  )
}

export default NovaOcorrenciaModal
